// Fundo da folha das páginas (0.65): linhas (pautada, com margem, quadriculada, pontilhada) e temas decorativos
// bem clarinhos para escrever por cima. Tudo em SVG com padrões repetidos (leve, em qualquer tamanho de folha).
import { Circle, Defs, G, Line, Path, Pattern, Rect } from 'react-native-svg';
import type { Folha } from '../store/types';

const PASSO = 40;   // distância entre as linhas (na escala do quadro: 1000 = largura da tela)

type Tema = NonNullable<Folha['tema']>;
type Cores3 = [string, string, string];
/** "Cor do desenho: Automática" (0.67.2): as cores próprias de cada tema [principal, segunda, detalhe], na folha clara e na escura. */
const CORES_TEMA: Record<Tema, { clara: Cores3; escura: Cores3 }> = {
  floral: { clara: ['#EC4899', '#F59E0B', '#16A34A'], escura: ['#F9A8D4', '#FDE68A', '#86EFAC'] },         // flores rosadas, miolo amarelo, cabinho verde
  aquarela: { clara: ['#8B5CF6', '#EC4899', '#38BDF8'], escura: ['#C4B5FD', '#F9A8D4', '#7DD3FC'] },       // manchas lilás, rosa e azul
  estrelas: { clara: ['#F59E0B', '#2563EB', '#3B82F6'], escura: ['#FDE68A', '#93C5FD', '#BFDBFE'] },       // estrelas douradas num céu azul
  folhas: { clara: ['#16A34A', '#65A30D', '#15803D'], escura: ['#86EFAC', '#BEF264', '#4ADE80'] },         // folhas verdes
  geometrico: { clara: ['#0891B2', '#F97316', '#7C3AED'], escura: ['#67E8F9', '#FDBA74', '#C4B5FD'] },     // formas azul-piscina, laranja e roxo
  infantil: { clara: ['#EF4444', '#F59E0B', '#38BDF8'], escura: ['#FCA5A5', '#FDE68A', '#7DD3FC'] },       // coração vermelho, balão amarelo, nuvem azul
  minimalista: { clara: ['#6B7280', '#6B7280', '#6B7280'], escura: ['#D1D5DB', '#D1D5DB', '#D1D5DB'] },
};
/** Cores do desenho do tema: as próprias do tema (Automática) ou a cor escolhida em todo o desenho. */
export function coresDoTema(folha: Folha, escura: boolean): Cores3 | null {
  if (!folha.tema) return null;
  if (folha.corTema) return [folha.corTema, folha.corTema, folha.corTema];
  return CORES_TEMA[folha.tema][escura ? 'escura' : 'clara'];
}

/** Desenho de um "ladrilho" do tema (400 x 400), repetido pela folha. */
function Ladrilho({ tema, cores }: { tema: Tema; cores: Cores3 }) {
  const [cor, cor2, cor3] = cores;
  switch (tema) {
    case 'floral': {
      const flor = (x: number, y: number, r: number) => (
        <G key={`${x}-${y}`}>
          {[0, 72, 144, 216, 288].map(a => {
            const rad = (a * Math.PI) / 180;
            return <Circle key={a} cx={x + Math.cos(rad) * r} cy={y + Math.sin(rad) * r} r={r * 0.75} fill={cor} />;
          })}
          <Circle cx={x} cy={y} r={r * 0.55} fill={cor2} />
        </G>
      );
      return <G>{flor(70, 80, 16)}{flor(290, 150, 12)}{flor(170, 300, 20)}{flor(350, 350, 9)}
        <Path d="M70 100 Q80 160 60 200 M170 320 Q160 370 190 400" stroke={cor3} strokeWidth={3} fill="none" /></G>;
    }
    case 'aquarela':
      return <G>
        <Circle cx={90} cy={110} r={90} fill={cor} opacity={0.55} />
        <Circle cx={300} cy={260} r={120} fill={cor2} opacity={0.4} />
        <Circle cx={180} cy={360} r={60} fill={cor3} opacity={0.5} />
      </G>;
    case 'estrelas': {
      const estrela = (x: number, y: number, r: number) => {
        const pts: string[] = [];
        for (let i = 0; i < 10; i++) { const a = (Math.PI / 5) * i - Math.PI / 2, rr = i % 2 ? r * 0.45 : r; pts.push(`${x + Math.cos(a) * rr} ${y + Math.sin(a) * rr}`); }
        return <Path key={`${x}-${y}`} d={`M${pts.join('L')}Z`} fill={cor} />;
      };
      return <G>{estrela(60, 70, 14)}{estrela(250, 40, 8)}{estrela(330, 190, 16)}{estrela(130, 260, 10)}{estrela(300, 360, 7)}
        {[[180, 120], [40, 330], [370, 90], [220, 300], [100, 180]].map(([x, y]) => <Circle key={`${x}${y}`} cx={x} cy={y} r={3} fill={cor2} />)}</G>;
    }
    case 'folhas': {
      const folha = (x: number, y: number, ang: number, k: number, c = cor) => (
        <G key={`${x}-${y}`} transform={`translate(${x} ${y}) rotate(${ang}) scale(${k})`}>
          <Path d="M0 0 C20 -30 60 -30 80 0 C60 30 20 30 0 0Z" fill={c} />
          <Path d="M0 0 L80 0" stroke={cor3} strokeWidth={2} opacity={0.6} />
        </G>
      );
      return <G>{folha(40, 90, -20, 1)}{folha(240, 60, 25, 0.8, cor2)}{folha(150, 250, -40, 1.2)}{folha(300, 330, 10, 0.9, cor2)}
        <Path d="M20 380 Q120 300 200 380" stroke={cor3} strokeWidth={3} fill="none" /></G>;
    }
    case 'geometrico':
      return <G fill="none" stroke={cor} strokeWidth={3}>
        <Path d="M40 40 L110 40 L75 100Z" />
        <Rect x={240} y={60} width={70} height={70} transform="rotate(20 275 95)" stroke={cor2} />
        <Path d="M120 250 l35 -20 l35 20 l0 40 l-35 20 l-35 -20Z" stroke={cor3} />
        <Circle cx={330} cy={320} r={34} stroke={cor2} />
      </G>;
    case 'infantil':
      return <G>
        <Path d="M60 120 C30 90 60 50 80 80 C100 50 130 90 100 120 L80 140Z" fill={cor} />
        <Circle cx={280} cy={80} r={28} fill={cor2} /><Path d="M280 108 Q290 150 270 190" stroke={cor2} strokeWidth={2} fill="none" />
        <Path d="M150 320 a30 30 0 0 1 50 -20 a25 25 0 0 1 45 15 a20 20 0 0 1 -5 40 h-85 a20 20 0 0 1 -5 -35Z" fill={cor3} />
        <Circle cx={340} cy={300} r={10} fill={cor2} />
      </G>;
    case 'minimalista':
      return <G stroke={cor} strokeWidth={2}>
        <Line x1={20} y1={20} x2={80} y2={20} /><Line x1={20} y1={20} x2={20} y2={80} />
        <Circle cx={300} cy={300} r={4} fill={cor} /><Circle cx={320} cy={300} r={4} fill={cor} /><Circle cx={340} cy={300} r={4} fill={cor} />
      </G>;
  }
}

/** Fundo da folha, desenhado dentro de um Svg na escala do quadro. Cobre o retângulo (x, y, larg, alt).
 *  0.72 (texto 11c B12): as linhas e os pontos são desenhados como caminhos só na área pedida (o quadro pede a área
 *  VISÍVEL, em qualquer zoom), sempre presos à mesma grade (0, 40, 80...), então não há emendas ao rolar ou dar zoom.
 *  O tema continua em ladrilhos de 400 presos ao canto da folha. */
export function FolhaFundo({ folha, larg, alt, fundo, escura, idBase, x = 0, y = 0 }: { folha: Folha; larg: number; alt: number; fundo: string; escura: boolean; idBase: string; x?: number; y?: number }) {
  const linha = escura ? 'rgba(255,255,255,0.13)' : 'rgba(30,58,138,0.16)';
  const margem = escura ? 'rgba(248,113,113,0.35)' : 'rgba(220,38,38,0.35)';
  const temaCores = coresDoTema(folha, escura);   // 0.67.2: cores próprias do tema (Automática) ou a cor escolhida
  const opTema = Math.max(0.03, Math.min(0.5, folha.forca * 0.35));
  const tid = `t-${idBase}`;
  const x1 = x + larg, y1 = y + alt;
  const r = (n: number) => Math.round(n * 10) / 10;
  // primeira posição da grade (passo 40, deslocada de off) a partir de v
  const de = (v: number, off: number) => Math.ceil((v - off) / PASSO) * PASSO + off;
  let d = '';
  // texto 20 (3): páginas novas têm um espaço antes da 1ª linha (folha.topo); as antigas continuam como eram
  const topo = folha.topo;
  if (folha.tipo === 'pautada' || folha.tipo === 'margem') {
    for (let yy = Math.max(de(y, PASSO - 1), (topo ?? 80) + PASSO - 1); yy <= y1; yy += PASSO) d += `M${r(x)} ${yy}H${r(x1)}`;
  } else if (folha.tipo === 'quadriculada') {
    const y0 = Math.max(y, topo ?? y);
    for (let yy = de(y0, 0); yy <= y1; yy += PASSO) d += `M${r(x)} ${yy}H${r(x1)}`;
    if (y1 > y0) for (let xx = de(x, 0); xx <= x1; xx += PASSO) d += `M${xx} ${r(y0)}V${r(y1)}`;
  }
  const pid = `p-${idBase}`;   // pontilhada: ladrilho de 40 com o ponto no meio (preso ao canto da folha, sem emendas)
  return (
    <G>
      {(!!folha.tema || folha.tipo === 'pontilhada') && (
        <Defs>
          {!!folha.tema && (
            <Pattern id={tid} x={0} y={0} width={400} height={400} patternUnits="userSpaceOnUse">
              <G opacity={opTema}><Ladrilho tema={folha.tema} cores={temaCores!} /></G>
            </Pattern>
          )}
          {folha.tipo === 'pontilhada' && (
            <Pattern id={pid} x={0} y={0} width={PASSO} height={PASSO} patternUnits="userSpaceOnUse">
              <Circle cx={PASSO / 2} cy={PASSO / 2} r={2.2} fill={linha} />
            </Pattern>
          )}
        </Defs>
      )}
      <Rect x={x} y={y} width={larg} height={alt} fill={fundo} />
      {!!folha.tema && <Rect x={x} y={y} width={larg} height={alt} fill={`url(#${tid})`} />}
      {!!d && <Path d={d} stroke={linha} strokeWidth={folha.tipo === 'quadriculada' ? 1.2 : 1.5} fill="none" />}
      {folha.tipo === 'pontilhada' && (topo == null || y1 > topo) && <Rect x={x} y={Math.max(y, topo ?? y)} width={larg} height={y1 - Math.max(y, topo ?? y)} fill={`url(#${pid})`} />}
      {folha.tipo === 'margem' && x <= 91 && <Line x1={90} y1={y} x2={90} y2={y1} stroke={margem} strokeWidth={2} />}
    </G>
  );
}
